/**
 * Confirm it's you, on the tenant side — addendum §G.4.
 *
 * The same shape as Rowted Admin's `pages/admin/step-up.tsx`, and deliberately
 * a second file rather than a shared one, because the two doors ask for
 * different things: an admin's step-up is a six-digit code, and a tenant
 * user's is their password — `POST /auth/step-up` takes `{ password }` and
 * nothing else. One component trying to be both would have to know which side
 * it was on, and would be wrong on the first day either endpoint changed.
 *
 * **The request is replayed, not re-triggered.** `guard(fn)` runs `fn`; if the
 * server answers `step_up_required` it holds `fn` aside, asks for the
 * password, posts it, and runs `fn` again. The caller writes its action once
 * and never mentions step-up. Before this, `TwoStepSetup` printed the server's
 * "Confirm your password to continue" into its own error band with no field to
 * confirm it in — so the whole dialog dead-ended for anybody whose session was
 * not already fresh, which is almost everybody.
 *
 * §4.4 wants the sign-in panels here eventually, so a passkey can answer a
 * step-up too. That needs `/auth/step-up` to take a method answer; until it
 * does, offering a panel the server would refuse would be worse than asking
 * for the one thing it accepts.
 */
import { useCallback, useRef, useState, type ReactNode } from 'react'
import { ApiError } from '@rowted/shared'
import { request } from '../lib/api.js'
import { Button } from './primitives.js'
import { Field } from './fields.js'
import { Modal, STEP_UP_LAYER } from './overlays.js'

export interface StepUp {
  /**
   * Run something that may need the password first.
   *
   * Resolves true when it ran, false when the person closed the dialog. Any
   * other failure is thrown — this hook knows one error code and nothing else.
   */
  guard: (action: () => Promise<void>) => Promise<boolean>
  /** Render this once, anywhere in the tree. */
  dialog: ReactNode
}

export function useStepUp(): StepUp {
  const [open, setOpen] = useState(false)
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const held = useRef<{
    action: () => Promise<void>
    settle: (ran: boolean) => void
    fail: (error: unknown) => void
  } | null>(null)

  const close = useCallback(() => {
    setOpen(false)
    setPassword('')
    setError(null)
    held.current?.settle(false)
    held.current = null
  }, [])

  const guard = useCallback((action: () => Promise<void>) =>
    new Promise<boolean>((settle, fail) => {
      action().then(() => settle(true)).catch((caught: unknown) => {
        if (caught instanceof ApiError && caught.code === 'step_up_required') {
          held.current = { action, settle, fail }
          setError(null)
          setPassword('')
          setOpen(true)
          return
        }
        fail(caught)
      })
    }), [])

  const confirm = useCallback(async () => {
    const waiting = held.current
    if (!waiting || !password) return
    setBusy(true)
    setError(null)
    try {
      await request('/auth/step-up', { method: 'POST', body: { password } })
    } catch (caught) {
      setBusy(false)
      setError(caught instanceof ApiError ? caught.message : 'That did not work. Try again.')
      return
    }

    /*
     * The replay is outside the try above on purpose: a failure here belongs
     * to the action, not to the password, and reporting it in this dialog
     * would tell somebody their password was wrong when it was not.
     */
    try {
      await waiting.action()
      waiting.settle(true)
    } catch (caught) {
      waiting.fail(caught)
    } finally {
      held.current = null
      setBusy(false)
      setOpen(false)
      setPassword('')
    }
  }, [password])

  const dialog = open ? (
    <Modal
      open
      onClose={close}
      layer={STEP_UP_LAYER}
      width={400}
      title="Confirm it’s you"
      footer={(
        <>
          <Button onClick={close}>Cancel</Button>
          <Button variant="primary" disabled={!password || busy} onClick={() => void confirm()}>
            Continue
          </Button>
        </>
      )}
    >
      <div className="flex flex-col gap-3 p-4">
        <p className="m-0 text-body leading-[1.45] text-text-muted">
          Changing how you sign in asks for your password again.
        </p>
        {error && (
          <p role="alert" className="m-0 rounded-field bg-danger-bg px-3 py-2 text-cell
                                     text-danger">
            {error}
          </p>
        )}
        <Field
          label="Password"
          type="password"
          value={password}
          onChange={setPassword}
          autoFocus
          required
        />
      </div>
    </Modal>
  ) : null

  return { guard, dialog }
}
